import { apiSlice } from '../../../store/api/apiSlice';
import type {
  CalendarChangeDetail,
  CalendarChangePage,
  CalendarChangeRuntimeControl,
  CalendarChangeScope,
  CalendarChangeState,
  ReschedulingAggregate,
  ResolveCalendarChangeCommand,
  SetCalendarChangeSyncCommand,
} from '../types';

interface CalendarChangeListRequest {
  coachPersonId?: string;
  scope?: CalendarChangeScope;
  state?: CalendarChangeState;
  reason?: string;
  from?: string;
  to?: string;
  cursor?: string | null;
  limit?: number;
}

interface AggregateRequest {
  from?: string;
  to?: string;
}

const cleanParams = (request?: Record<string, unknown>): Record<string, unknown> => {
  if (!request) return {};
  return Object.fromEntries(
    Object.entries(request).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );
};

export const adminCalendarChangesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCalendarChanges: builder.query<CalendarChangePage, CalendarChangeListRequest | undefined>({
      query: (request) => ({
        url: '/admin/calendar-changes',
        params: cleanParams({
          coach_person_id: request?.coachPersonId,
          scope: request?.scope,
          state: request?.state,
          reason: request?.reason,
          from: request?.from,
          to: request?.to,
          cursor: request?.cursor,
          limit: request?.limit,
        }),
      }),
      providesTags: ['CalendarChanges'],
    }),
    getCalendarChange: builder.query<CalendarChangeDetail, string>({
      query: (changeId) => `/admin/calendar-changes/${encodeURIComponent(changeId)}`,
      providesTags: (_result, _error, changeId) => [{ type: 'CalendarChanges', id: changeId }],
    }),
    getCalendarChangeReviewQueue: builder.query<
      CalendarChangePage,
      { cursor?: string | null; limit?: number } | undefined
    >({
      query: (request) => ({
        url: '/admin/calendar-changes/review',
        params: cleanParams({ cursor: request?.cursor, limit: request?.limit }),
      }),
      providesTags: ['CalendarChanges'],
    }),
    getCalendarChangeAggregate: builder.query<ReschedulingAggregate, AggregateRequest | undefined>({
      query: (request) => ({
        url: '/admin/calendar-changes/aggregate',
        params: cleanParams({ from: request?.from, to: request?.to }),
      }),
      providesTags: ['CalendarChanges'],
    }),
    resolveCalendarChange: builder.mutation<CalendarChangeDetail, ResolveCalendarChangeCommand>({
      query: ({ changeId, kind, note }) => ({
        url: `/admin/calendar-changes/${encodeURIComponent(changeId)}/resolve`,
        method: 'POST',
        body: { kind, note },
      }),
      invalidatesTags: ['CalendarChanges'],
    }),
    getCalendarChangeRuntime: builder.query<CalendarChangeRuntimeControl, void>({
      query: () => '/admin/calendar-changes/runtime',
      providesTags: ['CalendarChanges'],
    }),
    setCalendarChangeSync: builder.mutation<
      CalendarChangeRuntimeControl,
      SetCalendarChangeSyncCommand
    >({
      query: ({ enabled, reason }) => ({
        url: '/admin/calendar-changes/runtime',
        method: 'POST',
        body: { enabled, reason },
      }),
      invalidatesTags: ['CalendarChanges'],
    }),
  }),
});

export const {
  useGetCalendarChangesQuery,
  useGetCalendarChangeQuery,
  useGetCalendarChangeReviewQueueQuery,
  useGetCalendarChangeAggregateQuery,
  useResolveCalendarChangeMutation,
  useGetCalendarChangeRuntimeQuery,
  useSetCalendarChangeSyncMutation,
} = adminCalendarChangesApi;
